import store from './store'
import { AGlobalConfig } from 'kuaidong-api'
import { getModule, Module, Mutation, VuexModule, Action } from 'vuex-module-decorators'

import {
  ISubCategory,
  IFirstCategory,
  IGlobalConfigDrills,
  IGlobalConfigHomework,
  HomeworkTemplateItem,
  HomeworkSelectScopeItem,
  HomeworkSelectScopeDrill,
} from 'kuaidong-model'

@Module({
  namespaced: true,
  dynamic: true,
  store,
  name: 'global-config',
})
class GlobalConfigModule extends VuexModule {
  firstTag: string | null = null

  subDrill: ISubCategory | null = null

  drills: IGlobalConfigDrills | null = null

  homework: IGlobalConfigHomework | null = null

  drillCategories: IFirstCategory[] | null = null

  drillsPromise: Promise<IGlobalConfigDrills> | null = null

  homeworkPromise: Promise<IGlobalConfigHomework> | null = null

  drillCategoriesPromise: Promise<IFirstCategory[]> | null = null
  summerCompetition2022Promise: Promise<{ unread_count: number }> | null = null
  isHasNewSummerCompetition2022 = false

  @Mutation
  reset() {
    this.firstTag = null
    this.subDrill = null

    this.drills = null
    this.homework = null
    this.drillCategories = null

    this.drillsPromise = null
    this.homeworkPromise = null
    this.drillCategoriesPromise = null
  }

  @Action
  async ensureConfigIsLoaded() {
    return Promise.all([
      this.ensureDrillsIsLoaded(),
      this.ensureHomeworkIsLoaded(),
      this.ensureDrillCategoriesIsLoaded(),
    ])
  }

  @Action
  async ensureDrillsIsLoaded() {
    if (this.drillsPromise === null) {
      this.loadDrills()
    }

    return this.drillsPromise
  }

  @Action
  async ensureHomeworkIsLoaded() {
    if (this.homeworkPromise === null) {
      this.loadHomework()
    }

    return this.homeworkPromise
  }

  @Action
  async ensureDrillCategoriesIsLoaded() {
    if (this.drillCategoriesPromise === null) {
      this.loadDrillCategories()
    }

    return this.drillCategoriesPromise
  }

  @Action
  async loadDrills() {
    const drillsPromise = AGlobalConfig.allDrills()

    this.context.commit('setDrillsPromise', drillsPromise)

    this.context.commit('setDrills', await drillsPromise)
  }

  @Action
  async loadHomework() {
    const homeworkPromise = AGlobalConfig.homework()

    this.context.commit('setHomeworkPromise', homeworkPromise)

    this.context.commit('setHomework', await homeworkPromise)
  }

  @Action
  async loadDrillCategories() {
    const drillCategoriesPromise = AGlobalConfig.getDrillCategory()

    this.context.commit('setDrillCategoriesPromise', drillCategoriesPromise)

    this.context.commit('setDrillCategories', await drillCategoriesPromise)
  }

  @Mutation
  setDrills(drills: IGlobalConfigDrills) {
    this.drills = drills
  }

  @Mutation
  setHomework(homework: IGlobalConfigHomework) {
    this.homework = homework
  }

  @Mutation
  setDrillCategories(drillCategories: IFirstCategory[]) {
    this.drillCategories = drillCategories
  }

  @Mutation
  setDrillsPromise(drillsPromise: Promise<IGlobalConfigDrills>) {
    this.drillsPromise = drillsPromise
  }

  @Mutation
  setHomeworkPromise(homeworkPromise: Promise<IGlobalConfigHomework>) {
    this.homeworkPromise = homeworkPromise
  }

  @Mutation
  setDrillCategoriesPromise(drillCategoriesPromise: Promise<IFirstCategory[]>) {
    this.drillCategoriesPromise = drillCategoriesPromise
  }

  @Mutation
  updateFirstTag(tag: string) {
    this.firstTag = tag
  }

  @Mutation
  updateSubDrill(subDrill: ISubCategory) {
    this.subDrill = subDrill
  }

  @Mutation
  deleteDrillTag() {
    this.firstTag = null
    this.subDrill = null
  }

  get containerTypes(): HomeworkSelectScopeItem[] {
    return this.homework?.homework_select_scope || []
  }

  get containerTypeOptions(): { value: string; label: string }[] {
    return this.containerTypes.map(({ name, title }) => ({
      value: name,
      label: title,
    }))
  }

  get drillOptions(): (item: HomeworkSelectScopeItem) => { value: string; label: string }[] {
    return (item) =>
      item.drills.map(({ drill_id, title }) => ({
        value: drill_id,
        label: title,
      }))
  }

  get repeatOptions(): (item: HomeworkSelectScopeDrill) => { value: number; label: string }[] {
    return (item) =>
      new Array(item.max - item.min + 1).fill(item.min).map((d, i) => ({ value: i + d, label: `${i + d}组` }))
  }

  get drillItemOptions() {
    return this.containerTypeOptions.map((containerTypeOption) => {
      const containerType = this.findContainerType(containerTypeOption.value)
      const children = containerType
        ? this.drillOptions(containerType).map((drillOption) => {
            const drill = this.findDrill(containerTypeOption.value, drillOption.value)
            return {
              ...drillOption,
              childDefaultValue: drill?.default,
            }
          })
        : []

      return {
        ...containerTypeOption,
        children,
        childDefaultValue: containerType?.default_drill_id,
      }
    })
  }

  get homeworkItemOptions() {
    return this.containerTypeOptions.map((containerTypeOption) => {
      const containerType = this.findContainerType(containerTypeOption.value)
      const children = containerType
        ? this.drillOptions(containerType).map((drillOption) => {
            const drill = this.findDrill(containerTypeOption.value, drillOption.value)
            const children = drill ? this.repeatOptions(drill) : []
            return {
              ...drillOption,
              children,
              childDefaultValue: drill?.default,
            }
          })
        : []

      return {
        ...containerTypeOption,
        children,
        childDefaultValue: containerType?.default_drill_id,
      }
    })
  }

  get drillIdContainerTypeMap() {
    return this.drillItemOptions.reduce((result: { [index: string]: string }, container) => {
      for (const drill of container.children) {
        result[drill.value] = container.value
      }

      return result
    }, {})
  }

  get findContainerType(): (name: string) => HomeworkSelectScopeItem | undefined {
    return (name) => this.containerTypes.find((item) => item.name === name)
  }

  get findDrill(): (containerType: string, drillId: string) => HomeworkSelectScopeDrill | undefined {
    return (containerType, drillId) => {
      const item = this.findContainerType(containerType)
      return item ? item.drills.find((d) => d.drill_id === drillId) : undefined
    }
  }

  get findDrillById(): (drillId: string) => HomeworkSelectScopeDrill | undefined {
    return (drillId) => {
      for (const type of this.containerTypes) {
        for (const drill of type.drills) {
          if (drill.drill_id === drillId) {
            return drill
          }
        }
      }
    }
  }

  get findDefaultDrill(): (containerType: string) => HomeworkSelectScopeDrill | null {
    return (containerType) => {
      const item = this.findContainerType(containerType)
      if (!item || !item.drills.length) {
        return null
      }
      return (item.default_drill_id && item.drills.find((x) => x.drill_id === item.default_drill_id)) || item.drills[0]
    }
  }

  get findDefaultTemplateItems(): () => HomeworkTemplateItem[] {
    return () => {
      return this.homework?.default_homework || []
    }
  }

  get isRestDrill(): (drill: Pick<HomeworkSelectScopeDrill, 'drill_id'>) => boolean {
    return (drill) => drill.drill_id.startsWith('rest')
  }

  get allHomeworkDrills() {
    return this.drills
      ? this.allHomeworkDrillIds.filter((drillId) => !!this.drills![drillId]).map((drillId) => this.drills![drillId])
      : []
  }

  get allHomeworkDrillIds() {
    return (
      this.homework?.homework_select_scope.reduce((result: string[], container) => {
        result.push(...container.drills.map((drill) => drill.drill_id))
        return result
      }, []) || []
    )
  }

  get drillIdCategoriesMap() {
    return (
      this.drillCategories?.reduce(
        (result: { [index: string]: [string, string] }, category) => {
          category.children.forEach((subCategory) => {
            subCategory.drill_ids.forEach((drillId) => {
              result[drillId] = [category.name, subCategory.name]
            })
          })

          return result
        },
        { rest_2007: ['休息', ''] }
      ) || { rest_2007: ['休息', ''] }
    )
  }

  get drillItemOptionsCategory() {
    return (
      this.drillCategories?.map((category) => {
        const children = category.children.map((subCategory) => {
          const drills = subCategory.drill_ids
            .filter((drillId) => !!this.drills![drillId])
            .map((drillId) => {
              const drill = this.drills![drillId]!

              return {
                label: drill.display_name,
                value: drill.drill_id,
              }
            })

          return {
            label: subCategory.label,
            value: subCategory.name,
            children: drills,
          }
        })

        return {
          label: category.label,
          value: category.name,
          children,
        }
      }) || []
    )
  }
}

export default getModule(GlobalConfigModule)
